import "../css/journay.css"

import {createApp, ref, computed} from 'vue'
import axios from 'axios'

const placeholder = '/wp-content/plugins/woocommerce/assets/images/placeholder.png'

const App = {
    setup() {
        const steps = ref([])
        const loading = ref(true)
        const error = ref(null)
        const currentPath = ref([])
        const isAtEnd = ref(false)
        const productsCount = ref(null)
        const productsCountLoading = ref(false)
        const currentSlug = ref('')
        const tileBg = window.CJ_OPTIONS?.tile_bg || '#fff'
        const loaderColor = window.CJ_OPTIONS?.loader_color || '#3b82f6'
        const textColor = window.CJ_OPTIONS?.text_color || '#222'

        const availableSteps = computed(() => {
            if (!currentPath.value.length) return steps.value
            const last = currentPath.value[currentPath.value.length - 1]
            return last.children || []
        })

        // Start
        const init = async () => {
            loading.value = true
            error.value = null
            try {
                const res = await axios.get('/wp-json/cj/v1/paths')
                steps.value = res.data
            } catch (e) {
                error.value = 'Nie udało się załadować ścieżek.'
            } finally {
                loading.value = false
            }
        }

        // Klik na kafelek
        const pickStep = async (step, level) => {
            currentPath.value = currentPath.value.slice(0, level)
            currentPath.value.push(step)
            currentSlug.value = step.slug
            if (step.children && step.children.length) {
                isAtEnd.value = false
                productsCount.value = null
            } else {
                isAtEnd.value = true
                productsCountLoading.value = true
                try {
                    const res = await axios.get('/wp-json/cj/v1/products', {
                        params: {path: step.id, onlyCount: 1}
                    })
                    productsCount.value = res.data.count ?? 0
                } catch (e) {
                    productsCount.value = 0
                } finally {
                    productsCountLoading.value = false
                }
            }
        }

        const backTo = (level) => {
            currentPath.value = currentPath.value.slice(0, level)
            isAtEnd.value = false
            productsCount.value = null
            productsCountLoading.value = false
            currentSlug.value = ''
        }

        // Mount
        init()

        return {
            steps, loading, error, currentPath,
            isAtEnd, productsCount, productsCountLoading, availableSteps,
            pickStep, backTo, placeholder, currentSlug, tileBg, loaderColor, textColor
        }
    },
    template: `
      <div class="mx-auto py-10">
        <!-- Progress bar -->
  <nav v-if="currentPath.length" class="hidden lg:flex items-center space-x-2 mb-6 text-sm text-gray-600">
        <span v-for="(step, idx) in currentPath" :key="step.id" class="flex items-center">
          <span class="cursor-pointer hover:underline" @click="backTo(idx)">
            {{ step.name }}
          </span>
          <span v-if="idx < currentPath.length - 1" class="mx-2">&rsaquo;</span>
        </span>
        </nav>

        <!-- Loader główny -->
        <div v-if="loading" class="text-center py-8">
          <span class="animate-spin inline-block h-8 w-8 border-4 border-t-4 border-gray-300 rounded-full"
                :style="{ borderTopColor: loaderColor }"></span>
          <p class="mt-4">Ładowanie ścieżek...</p>
        </div>
        <div v-if="error" class="bg-red-100 text-red-700 p-4 rounded mb-4">{{ error }}</div>

        <!-- Krok wyboru kafelków -->
        <div v-if="!loading && !isAtEnd">
          <div class="flex flex-col lg:flex-row justify-center items-center gap-6">
            <div v-for="(item, idx) in availableSteps" :key="item.id"
                 class="w-full lg:w-1/3 rounded-2xl shadow p-4 flex flex-col items-center cursor-pointer transition hover:scale-105 aspect-16/12"
                 @click="pickStep(item, currentPath.length)" :style="{ backgroundColor: tileBg }">
              <img :src="item.image ? item.image : placeholder"
                   alt=""
                   class="w-full aspect-video object-cover mb-4 rounded-xl shadow"/>
              <div class="font-semibold text-base lg:text-lg" :style="{ color: textColor }">{{ item.name }}</div>
              <div v-if="item.description" class="text-gray-600 text-sm text-center mt-4 mb-8">{{ item.description }}
              </div>
            </div>
          </div>
          <div v-if="!currentPath.length && !steps.length" class="text-gray-500 mt-6">
            Brak dostępnych ścieżek do wyboru.
          </div>
        </div>

        <!-- Koniec ścieżki: licznik produktów -->
        <div v-if="isAtEnd">
          <!-- Loader -->
          <div v-if="productsCountLoading" class="text-center py-8">
            <span class="animate-spin inline-block h-8 w-8 border-4 border-t-4 border-gray-300 rounded-full"
                  :style="{ borderTopColor: loaderColor }"></span>
            <p class="mt-4">Sprawdzanie dostępnych produktów...</p>
          </div>

          <!-- Brak produktów -->
          <div v-else-if="productsCount === 0" class="text-center py-10">
            <div class="text-gray-700 text-lg font-medium mb-4">Brak produktów w wybranej ścieżce</div>
            <button
                class="mt-4 px-4 py-2 rounded-xl border bg-gray-100 text-gray-700 shadow cursor-pointer hover:bg-gray-200"
                @click="backTo(0)">
              Zacznij od nowa
            </button>
          </div>

          <!-- Są produkty -->
          <div v-else class="text-center py-10">
            <div class="text-gray-700 text-lg font-medium mb-4">
              Znaleziono {{ productsCount }} produktów w tej ścieżce
            </div>
            <a :href="'/sciezka-klienta/' + currentSlug"
               class="mt-4 inline-block px-6 py-3 rounded-xl border text-white font-semibold shadow transition"
               :style="{ backgroundColor: loaderColor }">
              Zobacz produkty
            </a>
            <div>
              <button class="mt-6 px-4 py-2 rounded-xl border bg-gray-100 text-gray-700 shadow hover:bg-gray-200"
                      @click="backTo(0)">
                Zacznij od nowa
              </button>
            </div>
          </div>
        </div>
      </div>
    `
}

createApp(App).mount('#customer-journay-app')


